/**
 * Экран «Настройки» — §14.2.
 *
 * §14.2 перечисляет для него: таргеты, подписки и пороги, пользователей с
 * их доступом (D10) и расход LLM.
 *
 * **Цели и пороги задаются здесь — и только здесь.** До 11.09.2026 они
 * правились на экране «Контроль»; владелец попросил развести, и так же
 * делит экраны §14.2: здесь задают, в «Контроле» видят, что по ним
 * сработало. Одна цифра, которую можно поменять на двух экранах, — это
 * вопрос «где её поменяли и какая действует», и задавать его незачем.
 *
 * **Порог и цель — разные вещи, и они разведены на экране.** Порог
 * относится к операции («скидка дороже 3 000 ₽») и действует с момента
 * сохранения. Цель относится к метрике («средний чек не ниже 1 800 ₽»),
 * действует со следующего дня и хранит историю: по ней сравнивали, и
 * прошлый сигнал обязан остаться объяснимым (§15).
 *
 * **Подписки правятся в боте, а не здесь, и это сказано словами.**
 * Соблазн продублировать экран настроек в вебе велик — и он же означал
 * бы два места, где хранится «во сколько присылать сводку», с
 * неизбежным расхождением. Экран показывает состояние и отправляет туда,
 * где его меняют. Ссылка на бота — не отговорка, а единственная точка
 * правки.
 *
 * **Пользователи и доступ — платформенный экран, а не наш.** §13.3 ТЗ
 * платформы отдаёт его `platform-api`; сервис, нарисовавший свою копию,
 * завёл бы вторую модель доступа рядом с настоящей. Здесь только ссылка.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@shell/ui';
import { ErrorState, Loading } from '@shell/states';
import { formatDate } from '@shell/format';
import { telegramWebApp } from '@shell/miniapp';
import { financeApi } from '../api';
import type { ControlRules, FinanceMe, MetricDefinition } from '../api';
import { Section } from '../components';

/** Пороги событий. Экспорт — для портала: там это «Контроль → Пороги и цели». */
export function Thresholds({ rules }: { rules: ControlRules }) {
  const client = useQueryClient();
  const [draft, setDraft] = useState<Record<string, string>>({});
  const save = useMutation({
    mutationFn: financeApi.saveRule,
    onSuccess: () => client.invalidateQueries({ queryKey: ['finance', 'control'] }),
  });

  // Не таблица: три колонки с полем ввода и кнопкой не помещаются в
  // ширину телефона — страница уезжала вбок на 205 точек. Строка
  // правила переносится, и на узком экране поле с кнопкой встают под
  // названием события.
  return (
    <div className="rules">
      {rules.rules.map((rule) => (
        <div className="rule" key={rule.kind}>
          <div className="rule__name">
            {rule.title}
            {/* «По умолчанию» — не украшение: человек должен видеть,
                трогал ли он этот порог, иначе он не поймёт, почему
                сигналы приходят не так, как он ожидал. */}
            {rule.is_default && <span className="muted"> · по умолчанию</span>}
            {rule.nl_source && <div className="muted">Задано словами: «{rule.nl_source}»</div>}
          </div>
          <div className="rule__edit">
            <input
              className="input rule__input"
              inputMode="decimal"
              aria-label={`Порог: ${rule.title}`}
              value={draft[rule.kind] ?? rule.threshold ?? ''}
              onChange={(event) => setDraft({ ...draft, [rule.kind]: event.target.value })}
            />
            <span className="muted">₽</span>
            <Button
              small
              disabled={draft[rule.kind] === undefined || save.isPending}
              onClick={() =>
                save.mutate({
                  kind: rule.kind,
                  threshold: draft[rule.kind],
                  threshold_pct: rule.threshold_pct ?? undefined,
                })
              }
            >
              Сохранить
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}

/** Цели по метрикам. Экспорт — для портала, как `Thresholds`. */
export function Targets({ rules }: { rules: ControlRules }) {
  const client = useQueryClient();
  const [metric, setMetric] = useState(rules.metrics[0]?.code ?? '');
  const [value, setValue] = useState('');
  const [comparison, setComparison] = useState('gte');
  const save = useMutation({
    mutationFn: financeApi.saveTarget,
    onSuccess: () => {
      setValue('');
      client.invalidateQueries({ queryKey: ['finance', 'control'] });
    },
  });

  return (
    <>
      {rules.targets.length === 0 ? (
        <p className="note">
          Целей нет. Пока их нет, отклонения ищутся по норме — среднему за восемь таких же
          дней недели. Цель нужна там, где «как обычно» вас не устраивает.
        </p>
      ) : (
        <ul className="list">
          {rules.targets.map((target) => (
            <li key={target.metric_code}>
              <strong>{target.title}</strong> — {target.description}
              <span className="muted"> · с {formatDate(target.valid_from)}</span>
            </li>
          ))}
        </ul>
      )}

      <div className="form-row">
        <select
          className="input"
          aria-label="Метрика"
          value={metric}
          onChange={(event) => setMetric(event.target.value)}
        >
          {rules.metrics.map((item) => (
            <option key={item.code} value={item.code}>
              {item.title}
            </option>
          ))}
        </select>
        <select
          className="input"
          aria-label="Направление"
          value={comparison}
          onChange={(event) => setComparison(event.target.value)}
        >
          <option value="gte">не ниже</option>
          <option value="lte">не выше</option>
        </select>
        <input
          className="input"
          inputMode="decimal"
          aria-label="Значение цели"
          value={value}
          onChange={(event) => setValue(event.target.value)}
        />
        <Button
          small
          disabled={!value || save.isPending}
          onClick={() => save.mutate({ metric_code: metric, value, comparison })}
        >
          Поставить цель
        </Button>
      </div>
      <p className="note">
        Цель начинает действовать со следующего дня: сегодняшний уже идёт, и судить его
        целью, поставленной в обед, значило бы объявить нарушением половину дня.
      </p>
    </>
  );
}

export function Settings({ me }: { me: FinanceMe }) {
  const query = useQuery({ queryKey: ['finance', 'catalog'], queryFn: financeApi.catalog });
  const rules = useQuery({
    queryKey: ['finance', 'control'],
    queryFn: financeApi.controlRules,
  });

  return (
    <div className="screen">
      <Section title="Цели по метрикам">
        {rules.isLoading && <Loading />}
        {rules.error && <ErrorState error={rules.error} onRetry={() => rules.refetch()} />}
        {rules.data && <Targets rules={rules.data} />}
      </Section>

      <Section title="Пороги событий">
        {rules.data && <Thresholds rules={rules.data} />}
        <p className="note">
          Порог можно задать и фразой — в боте, в разделе «Контроль». Такой порог здесь
          помечен «задано словами». Что сработало по этим правилам, видно на экране
          «Контроль».
        </p>
      </Section>

      <Section title="Что и когда вам присылать">
        <p>
          Подписки на сводки, время каждой и тихие часы настраиваются в боте — командой{' '}
          <code>/настройки</code>.
        </p>
        <p className="faint">
          Настройка живёт в одном месте намеренно: два экрана, где задаётся время утренней
          сводки, рано или поздно разойдутся, и разойдутся молча.
        </p>
      </Section>

      <Section title="Метрики и формулы">
        <p className="faint">
          Формула каждой цифры хранится в базе и правится без выкладки кода. Это тот же
          текст, который показывается в блоке «как посчитано».
        </p>
        {query.isLoading && <Loading />}
        {query.error && <ErrorState error={query.error} onRetry={() => query.refetch()} />}
        {query.data && <MetricList metrics={query.data.metrics} />}
      </Section>

      <Section title="Пользователи и доступ">
        <p>
          Доступ к финансовому сервису выдаёт <strong>только владелец</strong> и только
          поимённо. Экран управления доступом — общий для всех трёх сервисов и живёт в
          платформе.
        </p>
        <p className="faint">
          Уровня «администратор» внутри финансового сервиса нет: допущенных единицы, и все
          допущены владельцем лично (D14).
        </p>
        {me.is_host ? (
          <AccessPanelLink
            adminUrl={me.admin_url ?? null}
            opsBot={me.ops_bot_username ?? null}
          />
        ) : (
          <p className="faint">Раздел доступен владельцу системы.</p>
        )}
      </Section>

      <Section title="Чего здесь пока нет">
        {/* §8 правил интерфейса: раздел, обещанный ТЗ и отсутствующий без
            объяснения, читается как «забыли». Названный условием — как план. */}
        <ul className="issues">
          <li>
            <strong>Расход на модель</strong> — появится, когда будет поднят шлюз к Claude.
            Сейчас считать нечего: обращений к модели нет.
          </li>
        </ul>
      </Section>
    </div>
  );
}

/** Названия групп метрик. Коды групп — слова из модели данных, и человеку
 *  они не показываются (§7 правил интерфейса): до 11.09.2026 третья группа
 *  была подписана служебным «control». */
const GROUP_TITLES: Record<string, string> = {
  revenue: 'Выручка и продажи',
  check: 'Средний чек и наполняемость',
  control: 'Контроль',
};

function MetricList({ metrics }: { metrics: MetricDefinition[] }) {
  const groups = new Map<string, MetricDefinition[]>();
  for (const metric of metrics) {
    const list = groups.get(metric.category) ?? [];
    list.push(metric);
    groups.set(metric.category, list);
  }

  return (
    <div className="metric-list">
      {[...groups.entries()].map(([category, list]) => (
        <div key={category}>
          <h3 className="metric-list__group">{GROUP_TITLES[category] ?? category}</h3>
          {list.map((metric) => (
            <details key={metric.code} className="metric-list__item">
              <summary>
                {metric.name} <span className="faint">· {metric.unit}</span>
              </summary>
              <pre className="explanation">{metric.formula}</pre>
            </details>
          ))}
        </div>
      ))}
    </div>
  );
}


/**
 * Куда ведёт «Пользователи и доступ». Экран живёт на адресе платформы
 * (`ops.`), а не в дашборде: до 11.09.2026 кнопка вела на `/users` самого
 * дашборда, такой страницы нет, и человек оказывался на «Обзоре».
 *
 * Внутри Telegram переход на чужой адрес потерял бы вход: подписанная
 * личность (`initData`) остаётся у дашборда. Поэтому там кнопка ведёт в
 * операционный бот, где «⚙️ Ещё → Пользователи и доступ» открывает панель
 * уже с входом. В браузере — обычная ссылка на панель.
 */
function AccessPanelLink({
  adminUrl,
  opsBot,
}: {
  adminUrl: string | null;
  opsBot: string | null;
}) {
  const telegram = telegramWebApp();
  if (telegram?.initData && opsBot && telegram.openTelegramLink) {
    const open = telegram.openTelegramLink;
    return (
      <>
        <Button small onClick={() => open(`https://t.me/${opsBot}`)}>
          Открыть «Пользователи и доступ»
        </Button>
        <p className="faint">
          Откроется операционный бот: там «⚙️ Ещё → Пользователи и доступ».
        </p>
      </>
    );
  }
  if (adminUrl) {
    return (
      <a
        className="button button--quiet button--small"
        href={adminUrl}
        target="_blank"
        rel="noreferrer"
      >
        Открыть «Пользователи и доступ»
      </a>
    );
  }
  return (
    <p className="faint">
      Адрес панели доступа на этом сервере не задан — её можно открыть из операционного бота:
      «⚙️ Ещё → Пользователи и доступ».
    </p>
  );
}
